import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { useCart, ZAR } from "@/lib/cart";
import ShareButtons from "@/components/ShareButtons";
import { X, Plus, Minus, ArrowRight } from "@phosphor-icons/react";

/**
 * Quick View modal — loads a product by slug and lets user pick variants & add to cart
 * without leaving the shop grid. Keeps focus on browsing flow.
 */
export default function ProductQuickView({ slug, open, onClose }) {
  const [product, setProduct] = useState(null);
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const cart = useCart();

  useEffect(() => {
    if (!open || !slug) return;
    setProduct(null);
    setImgIdx(0);
    setQty(1);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      const initial = {};
      (r.data.variant_options || []).forEach((o) => (initial[o.name] = o.values?.[0]));
      setSelected(initial);
    });
  }, [slug, open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const canAdd = useMemo(() => {
    if (!product) return false;
    return (product.variant_options || []).every((o) => selected[o.name]);
  }, [product, selected]);

  const addToCart = () => {
    if (!product) return;
    cart.add({
      product_slug: product.slug,
      name: product.name,
      unit_price: product.price,
      quantity: qty,
      variant: Object.keys(selected).length ? selected : null,
      image: product.images?.[0] || null,
    });
    toast.success("Added to cart", { description: `${product.name} × ${qty}` });
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            data-testid="quick-view-backdrop"
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            data-testid="quick-view-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-6 pointer-events-none"
          >
            <div className="pointer-events-auto w-full max-w-[900px] max-h-[90vh] overflow-y-auto border border-white/10 bg-[#0a0a0a] shadow-[0_40px_100px_rgba(0,0,0,0.7)]">
              {/* Header */}
              <div className="sticky top-0 flex items-center justify-between border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur px-4 sm:px-6 py-3 z-10">
                <span className="font-label text-[10px] text-[#00FFC2]">/ QUICK VIEW</span>
                <button
                  onClick={onClose}
                  data-testid="quick-view-close"
                  aria-label="Close"
                  className="inline-flex h-8 w-8 items-center justify-center border border-white/15 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>

              {!product ? (
                <div className="p-16 text-center text-sm text-white/40">Loading…</div>
              ) : (
                <div className="grid gap-6 p-4 sm:p-6 md:grid-cols-2">
                  {/* Image */}
                  <div>
                    <div className="relative aspect-square overflow-hidden border border-white/10 bg-gradient-to-b from-[#0d0d0d] to-black">
                      {product.images?.[imgIdx] ? (
                        <img
                          src={mediaUrl(product.images[imgIdx])}
                          alt={product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-white/20">no image</div>
                      )}
                    </div>
                    {product.images?.length > 1 && (
                      <div className="mt-3 flex gap-2 overflow-x-auto">
                        {product.images.map((img, i) => (
                          <button
                            key={i}
                            onClick={() => setImgIdx(i)}
                            className={`h-14 w-14 flex-shrink-0 overflow-hidden border transition-colors ${
                              i === imgIdx ? "border-[#FF5A1F]" : "border-white/15"
                            }`}
                          >
                            <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex flex-col">
                    <div className="font-label text-[10px] text-[#FF5A1F]">/ {product.brand}</div>
                    <h2 className="mt-2 font-display text-2xl font-black tracking-tighter">
                      {product.name}
                    </h2>

                    <div className="mt-4 flex items-baseline gap-3">
                      <div className="font-display text-3xl font-black text-[#00FFC2]">
                        {ZAR(product.price)}
                      </div>
                      {product.compare_at && product.compare_at > product.price && (
                        <div className="text-sm text-white/40 line-through">
                          {ZAR(product.compare_at)}
                        </div>
                      )}
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-white/60 line-clamp-4">
                      {product.description}
                    </p>

                    {/* Variants */}
                    {(product.variant_options || []).map((opt) => (
                      <div key={opt.name} className="mt-5">
                        <div className="font-label text-[10px] text-white/50 mb-2">
                          {opt.name}: <span className="text-white">{selected[opt.name]}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {opt.values.map((v) => {
                            const isSelected = selected[opt.name] === v;
                            return (
                              <button
                                key={v}
                                onClick={() => setSelected({ ...selected, [opt.name]: v })}
                                className={`border px-3 py-1.5 font-label text-[11px] transition-all ${
                                  isSelected
                                    ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-[#FF5A1F]"
                                    : "border-white/15 hover:border-white/40"
                                }`}
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    {/* Qty + Add */}
                    <div className="mt-6 flex gap-3">
                      <div className="flex items-center border border-white/15">
                        <button
                          onClick={() => setQty(Math.max(1, qty - 1))}
                          className="px-3 py-2 hover:bg-white/5"
                        >
                          <Minus size={12} />
                        </button>
                        <input
                          type="number"
                          min={1}
                          value={qty}
                          onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
                          className="w-10 bg-transparent text-center outline-none text-sm"
                        />
                        <button
                          onClick={() => setQty(qty + 1)}
                          className="px-3 py-2 hover:bg-white/5"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <button
                        data-testid="quick-view-add-to-cart"
                        disabled={!canAdd}
                        onClick={addToCart}
                        className="flex-1 bg-[#FF5A1F] py-2 font-label text-[11px] text-black transition-transform hover:-translate-y-0.5 disabled:opacity-40"
                      >
                        Add to cart · {ZAR(product.price * qty)}
                      </button>
                    </div>

                    <Link
                      to={`/product/${product.slug}`}
                      onClick={onClose}
                      data-testid="quick-view-full-details"
                      className="mt-3 inline-flex items-center gap-2 text-[10px] font-label text-white/60 hover:text-[#00FFC2] transition-colors"
                    >
                      View full details <ArrowRight size={12} weight="bold" />
                    </Link>

                    <div className="mt-4">
                      <ShareButtons
                        url={`https://vapeessence.co.za/product/${product.slug}`}
                        title={product.name}
                        text={`${product.name} · from R${product.price?.toFixed(0)} on Vape Essence`}
                        image={product.images?.[0] ? mediaUrl(product.images[0]) : undefined}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
